// The crosshair: four lines around the centre (CS style), optional centre dot and dark outline.
// Colour, line length, thickness and gap are the player's (Settings, Crosshair).

export interface CrosshairSettings {
  /** "#rrggbb". */
  color: string;
  /** Line length in pixels. */
  length: number;
  thickness: number;
  /** Space between the centre and each line. */
  gap: number;
  dot: boolean;
  outline: boolean;
}

export function defaultCrosshair(): CrosshairSettings {
  return { color: '#5cff7a', length: 7, thickness: 2, gap: 3, dot: false, outline: true };
}

export const CROSSHAIR_COLORS = [
  '#5cff7a',
  '#ffffff',
  '#00e5ff',
  '#ffe14a',
  '#ff4dd2',
  '#ff3b3b',
  '#f0893e',
  '#000000',
];

const int = (v: unknown, min: number, max: number, d: number) =>
  typeof v === 'number' && Number.isFinite(v) ? Math.max(min, Math.min(max, Math.round(v))) : d;

/** Saved settings -> complete, sane crosshair settings. */
export function sanitizeCrosshair(raw: unknown): CrosshairSettings {
  const d = defaultCrosshair();
  if (!raw || typeof raw !== 'object') return d;
  const o = raw as Partial<CrosshairSettings>;
  return {
    color: typeof o.color === 'string' && /^#[0-9a-f]{6}$/i.test(o.color) ? o.color.toLowerCase() : d.color,
    length: int(o.length, 0, 40, d.length),
    thickness: int(o.thickness, 1, 10, d.thickness),
    gap: int(o.gap, -5, 30, d.gap),
    dot: typeof o.dot === 'boolean' ? o.dot : d.dot,
    outline: typeof o.outline === 'boolean' ? o.outline : d.outline,
  };
}

/**
 * Draw a crosshair into an element holding four lines and a dot (<i> x 5). Positions are whole
 * pixels, so thin lines stay sharp.
 */
export function drawCrosshair(el: HTMLElement, c: CrosshairSettings) {
  const [right, left, down, up, dot] = [...el.children] as HTMLElement[];
  const t = c.thickness;
  const half = Math.floor(t / 2);
  const len = c.length;
  const place = (e: HTMLElement, x: number, y: number, w: number, h: number) => {
    e.style.left = x + 'px';
    e.style.top = y + 'px';
    e.style.width = w + 'px';
    e.style.height = h + 'px';
    e.style.display = w > 0 && h > 0 ? '' : 'none';
  };
  // The centre spans [-half, t - half); lines start `gap - 1` pixels from it on each side (gap 3 and
  // thickness 2 is the classic look), the same distance both ways for odd thicknesses too.
  const near = c.gap - 1;
  const far = t - half + near;
  const back = -half - near - len;
  place(right, far, -half, len, t);
  place(left, back, -half, len, t);
  place(down, -half, far, t, len);
  place(up, -half, back, t, len);
  place(dot, -half, -half, c.dot ? t : 0, c.dot ? t : 0);
  for (const e of [right, left, down, up, dot]) {
    e.style.background = c.color;
    e.style.boxShadow = c.outline ? '0 0 0 1px rgba(0, 0, 0, 0.7)' : 'none';
  }
}

const byId = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

/** The Crosshair settings group: preview, colour swatches and picker, sliders, toggles. */
export class CrosshairPanel {
  constructor(
    private readonly c: CrosshairSettings,
    private readonly changed: () => void,
  ) {
    const swatches = byId('chColors');
    for (const col of CROSSHAIR_COLORS) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'swatch';
      b.style.background = col;
      b.dataset.color = col;
      b.title = col;
      b.setAttribute('aria-label', 'Colour ' + col);
      b.addEventListener('click', () => this.set({ color: col }));
      swatches.append(b);
    }
    byId<HTMLInputElement>('chColor').addEventListener('input', (e) =>
      this.set({ color: (e.target as HTMLInputElement).value }),
    );
    for (const [id, key] of [
      ['chLength', 'length'],
      ['chThickness', 'thickness'],
      ['chGap', 'gap'],
    ] as const)
      byId<HTMLInputElement>(id).addEventListener('input', (e) =>
        this.set({ [key]: Number((e.target as HTMLInputElement).value) }),
      );
    byId<HTMLInputElement>('chDot').addEventListener('change', (e) =>
      this.set({ dot: (e.target as HTMLInputElement).checked }),
    );
    byId<HTMLInputElement>('chOutline').addEventListener('change', (e) =>
      this.set({ outline: (e.target as HTMLInputElement).checked }),
    );
    byId('chReset').addEventListener('click', () => this.set(defaultCrosshair()));
    this.show();
  }

  private set(p: Partial<CrosshairSettings>) {
    Object.assign(this.c, sanitizeCrosshair({ ...this.c, ...p }));
    this.show();
    this.changed();
  }

  /** Put the values in the controls and redraw the preview and the game's crosshair. */
  show() {
    const c = this.c;
    byId<HTMLInputElement>('chColor').value = c.color;
    byId<HTMLInputElement>('chLength').value = String(c.length);
    byId<HTMLInputElement>('chThickness').value = String(c.thickness);
    byId<HTMLInputElement>('chGap').value = String(c.gap);
    byId('chLengthVal').textContent = String(c.length);
    byId('chThicknessVal').textContent = String(c.thickness);
    byId('chGapVal').textContent = String(c.gap);
    byId<HTMLInputElement>('chDot').checked = c.dot;
    byId<HTMLInputElement>('chOutline').checked = c.outline;
    for (const b of document.querySelectorAll<HTMLElement>('#chColors .swatch'))
      b.classList.toggle('on', b.dataset.color === c.color);
    drawCrosshair(byId('chPreviewCross'), c);
    drawCrosshair(byId('cross'), c);
  }
}
